/**
 * EvalResultsGuideOverlay — B-2026-0057, B-2026-0085, B-2026-0086, B-2026-0088
 *
 * Athlete-facing Adaptive Guidance Overlay for the Evaluation Results view.
 * Renders via Portal (escapes stacking-context) at z-[10000].
 *
 * Layout: delegates to GuideDrawerLayout (DesktopDrawer / MobilePortraitSheet /
 * MobileLandscapePill) — no more duplicated layout code here.
 *
 * Content:
 *   - useGuideKey() resolves the active key via the B-2026-0086 priority cascade
 *     (activeSection scroll-spy → tap signals → 'default').
 *   - GuideCardContent renders the StructuredGuideBody (lead + bullets + lookFor).
 *   - Phase A3 wires AnchorHighlight using cardRef from GuideDrawerLayout.
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import {
  resolveGuideContent,
  resolveGuideBody,
  SECTION_TO_KEY,
  type EvalGuideKey,
  type EvalSummary,
} from '@/data/evaluationGuideContent';
import { GUIDE_DISMISSED_EVAL_RESULTS_KEY } from '@/utils/constants';
import { GuideCardContent } from './GuideCardContent';
import { GuideDrawerLayout } from './GuideDrawerLayout';
import { AnchorHighlight } from './AnchorHighlight';
import { useAnchorRect } from '@/hooks/useAnchorRect';

// ---------------------------------------------------------------------------
// Helper — derive guide key from context (B-2026-0086 priority cascade)
// ---------------------------------------------------------------------------

function useGuideKey(): EvalGuideKey | null {
  const { activeSection, selectedRegion, activePhase, focusedField } = useAdaptiveGuide();

  // Priority 1: section scroll-spy (topmost intersecting section).
  if (activeSection && activeSection in SECTION_TO_KEY) {
    return SECTION_TO_KEY[activeSection];
  }

  // Priority 2: tap/click signals from the page.
  if (selectedRegion) return 'region-selected';
  if (activePhase !== null) return 'phase-selected';
  if (focusedField === 'eval-video-compare') return 'view-mode-2d';
  if (focusedField === 'eval-score-breakdown') return 'body-form-score';

  // Priority 3: default overview.
  return null;
}

// ---------------------------------------------------------------------------
// Context hint helper
// ---------------------------------------------------------------------------

function useContextHint(): string {
  const { activeSection, selectedRegion, activePhase } = useAdaptiveGuide();
  if (activeSection) return 'Scroll to update · Tap a phase or region for targeted tips.';
  if (selectedRegion) return 'Showing guidance for the selected region.';
  if (activePhase !== null) return 'Showing guidance for the selected phase.';
  return 'Scroll to update · Tap a phase or region for targeted tips.';
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

interface EvalResultsGuideOverlayProps {
  /** Optional eval summary to pass to dynamic body resolvers (B-2026-0086). */
  evalSummary?: EvalSummary;
}

export function EvalResultsGuideOverlay({ evalSummary }: EvalResultsGuideOverlayProps) {
  const { isGuideOpen, setGuideOpen } = useAdaptiveGuide();
  const guideKey = useGuideKey();
  const entry = resolveGuideContent(guideKey);
  const body = resolveGuideBody(entry, evalSummary);
  const contextHint = useContextHint();
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Phase A3: anchor rect tracking
  const { rect: anchorRect, isOffscreen, isCovered } = useAnchorRect(
    guideKey ?? 'default',
    isGuideOpen,
  );

  // Measure the card panel rect for arrow source
  const [cardRect, setCardRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!isGuideOpen) { setCardRect(null); return; }
    const el = cardRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      setCardRect(el.getBoundingClientRect());
    });
    return () => cancelAnimationFrame(raf);
  }, [isGuideOpen, guideKey]);

  // Show arrow only when: anchor element exists, neither offscreen nor covered
  const showHighlight = isGuideOpen && anchorRect !== null && !isOffscreen && !isCovered;

  const handleDismiss = useCallback(() => {
    setGuideOpen(false);
    try {
      localStorage.setItem(GUIDE_DISMISSED_EVAL_RESULTS_KEY, 'true');
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }, [setGuideOpen]);

  if (!isGuideOpen) return null;

  return (
    <>
      <GuideDrawerLayout
        isOpen={isGuideOpen}
        onDismiss={handleDismiss}
        heading={entry.heading}
        contextHint={contextHint}
        cardRef={cardRef}
      >
        <GuideCardContent body={body} isOffscreen={isOffscreen} />
      </GuideDrawerLayout>
      <AnchorHighlight
        show={showHighlight}
        anchorRect={anchorRect}
        cardRect={cardRect}
      />
    </>
  );
}

export default EvalResultsGuideOverlay;
